import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Trophy, Star, Zap, Shield, Gift, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const BADGES = [
  { name: "Helper", threshold: 0, icon: Star, desc: "Nouveau membre du réseau." },
  { name: "Helper actif", threshold: 10, icon: Zap, desc: "Après 10 tâches complétées." },
  { name: "Helper fiable", threshold: 25, icon: Shield, desc: "25+ tâches et bonnes évaluations." },
  { name: "Helper Pro", threshold: 50, icon: Trophy, desc: "La crème de la crème : 50+ tâches." },
];

export default function Badges() {
  const { user } = useAuth();
  const count = user.helper_profile?.tasks_completed || 0;
  const current = user.helper_profile?.badge || "Helper";
  const refCode = user.referral_code || "";
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/register?ref=${refCode}` : "";
  const shareMessage = `Salut ! Rejoins-moi sur Go Helper et gagne de l'argent en aidant des gens près de chez toi. Avec mon code, on gagne 25 $ chacun à tes 3 premières tâches (et plus aux paliers 10 et 25). Inscris-toi : ${shareUrl}`;

  const copyCode = () => {
    navigator.clipboard.writeText(refCode);
    toast.success("Code copié !");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Rejoins-moi sur Go Helper", text: shareMessage, url: shareUrl });
      } catch {/* cancelled */}
    } else {
      navigator.clipboard.writeText(shareMessage);
      toast.success("Message copié !");
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-heading font-black text-navy">Statuts & Badges</h1>
        <p className="text-[#475569] mt-1">Plus tu complètes de tâches, plus tu gagnes en crédibilité.</p>
      </div>

      <div className="bg-navy text-white rounded-3xl p-8 relative overflow-hidden noise">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative">
          <div className="text-xs font-black uppercase tracking-wider text-lime">Ton statut actuel</div>
          <div className="font-heading font-black text-5xl mt-2" data-testid="current-badge">{current}</div>
          <div className="text-white/70 mt-2">{count} tâche(s) complétée(s)</div>
        </div>
      </div>

      {refCode && (
        <div className="bg-gradient-to-br from-lime/15 to-lime/5 border-2 border-lime rounded-3xl p-6 md:p-8" data-testid="badges-ref-code">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-lime text-navy flex items-center justify-center shrink-0">
              <Gift size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-black text-xl md:text-2xl text-navy">Ton code de parrainage</h2>
              <p className="text-[#475569] text-sm mt-1">
                Partage-le avec d'autres helpers. À chaque palier de tâches (3, 10, 25), vous gagnez tous les deux jusqu'à <strong>175 $</strong> chacun.
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <div className="bg-navy text-lime font-mono font-black tracking-widest text-xl rounded-full px-5 py-2.5" data-testid="badges-ref-value">
                  {refCode}
                </div>
                <button onClick={copyCode} className="bg-white border-2 border-navy text-navy font-bold rounded-full px-4 py-2.5 flex items-center gap-1 hover:bg-navy hover:text-white transition-colors" data-testid="badges-copy-code">
                  <Copy size={14} /> Copier
                </button>
                <button onClick={nativeShare} className="bg-lime text-navy font-bold rounded-full px-4 py-2.5 flex items-center gap-1 hover:scale-105 transition-transform" data-testid="badges-share-code">
                  <Share2 size={14} /> Partager
                </button>
              </div>
              <Link to="/referral" className="inline-block mt-3 text-navy font-bold text-sm hover:underline" data-testid="badges-ref-link">
                Voir mes filleuls et bonus →
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {BADGES.map((b) => {
          const Icon = b.icon;
          const earned = count >= b.threshold;
          return (
            <div key={b.name} className={`card-surface p-6 ${earned ? "ring-2 ring-lime" : "opacity-70"}`} data-testid={`badge-${b.name}`}>
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${earned ? "bg-lime text-navy" : "bg-surface text-[#94A3B8]"}`}>
                <Icon size={20} />
              </div>
              <div className="font-heading font-black text-xl text-navy mt-3">{b.name}</div>
              <div className="text-sm text-[#475569] mt-1">{b.desc}</div>
              {earned && <div className="chip-lime mt-3">Débloqué</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
